/**
 * Hooks for creating and returning loans.
 *
 * All mutations delegate to server-side RPCs — direct inserts into the `loans`
 * table must never be used from the frontend. Returns use a two-step handshake:
 * initiate then confirm. Either party (borrower or owner) can initiate; the
 * other must confirm. The owner can also force-complete a return they initiated.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { supabase } from '@/integrations/supabase/client';
import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';
import { promptPushRegistrationIfNeeded } from '@/hooks/usePushNotifications';

/**
 * Creates a loan proposal for an item via the `create_loan_proposal` RPC.
 *
 * Computes the optional due date client-side (today + `durationDays`) and passes
 * it to the RPC, which handles all circulation-policy enforcement. The loan starts
 * in PENDING_ACCEPTANCE status; the borrower must accept before it becomes ACTIVE.
 * On first successful loan, prompts the user for push-notification permission.
 *
 * @returns TanStack mutation accepting `{ itemId, borrowerId, durationDays?, groupId? }`.
 * @throws On network offline, circulation-policy violation, or rate-limit error (surfaced via toast).
 */
export function useCreateLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      borrowerId,
      durationDays,
      groupId,
    }: {
      itemId: string;
      borrowerId: string;
      durationDays?: number;
      groupId?: string;
    }) => {
      await assertOnline();
      // Compute due date if duration provided
      let dueAt: string | undefined;
      if (durationDays) {
        const d = new Date();
        d.setDate(d.getDate() + durationDays);
        dueAt = d.toISOString();
      }

      // Use the RPC function that enforces circulation rules and sets due_at atomically.
      // [LEARN] `as any` bypasses TypeScript's type checker — needed here because the auto-generated types don't include this RPC yet, but the call is safe.
      // `as any` casts are needed because the Supabase SDK's auto-generated types do
      // not yet include this RPC in the strongly-typed `.rpc()` overloads; the casts
      // are safe here — the parameter names match the DB function signature exactly.
      // [LEARN] RPC (Remote Procedure Call) delegates to a server-side function instead of a direct insert — the DB enforces circulation rules and rate limits atomically.
      const { data, error } = await (supabase.rpc('create_loan_proposal' as any, {
        p_item_id: itemId,
        p_borrower_id: borrowerId,
        p_due_at: dueAt ?? null,
        p_group_id: groupId ?? null,
      }) as any);

      if (error) throw error;

      return { loanId: data };
    },
    onSuccess: (data, variables) => {
      // Invalidate caches so UI shows fresh state
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      queryClient.invalidateQueries({ queryKey: ['group-activity'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      posthog.capture('loan_created', {
        item_id: variables.itemId,
        has_due_date: !!variables.durationDays,
      });
      // Prompt for push permission after first loan — fire-and-forget, silent on failure.
      promptPushRegistrationIfNeeded().catch(() => {});
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Initiates a loan return (first step of the two-step handshake).
 *
 * Calls the `initiate_loan_return` RPC, which sets the loan status to
 * RETURN_PENDING and notifies the other party (borrower or owner).
 *
 * @returns TanStack mutation accepting `{ loanId }`.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useInitiateReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, condition, notes }: { loanId: string; condition?: string; notes?: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('initiate_loan_return' as any, {
        p_loan_id: loanId,
        p_condition: condition ?? null,
        p_notes: notes ?? null,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_return_initiated', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Confirms a pending loan return (second step of the two-step handshake).
 *
 * Calls the `confirm_loan_return` RPC, which marks the loan as RETURNED and the
 * item as AVAILABLE. The owner can also force-complete a return they initiated.
 *
 * @returns TanStack mutation accepting `{ loanId }`.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useConfirmReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, condition, notes }: { loanId: string; condition?: string; notes?: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('confirm_loan_return' as any, {
        p_loan_id: loanId,
        p_condition: condition ?? null,
        p_notes: notes ?? null,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_return_confirmed', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Sets or updates the due date on an active loan (owner-only).
 *
 * Calls the `set_loan_due_date` RPC. Unlike `extend_loan_due_date` (which adds
 * N days to an existing due date), this sets an absolute date. Creates a
 * `loan_due_date_set` notification for the borrower.
 *
 * @returns TanStack mutation accepting `{ loanId, dueAt }` where `dueAt` is an ISO 8601 string.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useSetLoanDueDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, dueAt }: { loanId: string; dueAt: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('set_loan_due_date' as any, {
        p_loan_id: loanId,
        p_due_at: dueAt,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_due_date_set', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Cancels a pending loan return, reverting the loan status from RETURN_PENDING
 * back to ACTIVE via the `cancel_loan_return` RPC.
 *
 * @returns TanStack mutation accepting `{ loanId }`.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useCancelReturn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('cancel_loan_return' as any, { p_loan_id: loanId });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_return_cancelled', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Accepts a pending loan proposal (borrower-only).
 *
 * Calls the `accept_loan_proposal` RPC, transitioning the loan from
 * PENDING_ACCEPTANCE to ACTIVE and notifying the lender.
 *
 * @returns TanStack mutation accepting `{ loanId }`.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useAcceptLoanProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('accept_loan_proposal' as any, { p_loan_id: loanId });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_proposal_accepted', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Cancels a pending loan proposal (borrower declining or owner withdrawing).
 *
 * Calls the `cancel_loan_proposal` RPC, transitioning the loan from
 * PENDING_ACCEPTANCE to CANCELLED and resetting the item to AVAILABLE.
 *
 * @returns TanStack mutation accepting `{ loanId }`.
 * @throws On network offline or RPC error (surfaced via toast).
 */
export function useCancelLoanProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId }: { loanId: string }) => {
      await assertOnline();
      const { error } = await supabase.rpc('cancel_loan_proposal' as any, { p_loan_id: loanId });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_proposal_cancelled', { loan_id: variables.loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Extends a loan's due date by N days via the `extend_loan_due_date` RPC.
 *
 * @returns A React Query mutation accepting `{ loanId: string; additionalDays: number }`.
 * @throws Surfaces Supabase errors as user-facing toast messages via `mapSupabaseError`.
 */
export function useExtendLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, additionalDays }: { loanId: string; additionalDays: number }) => {
      await assertOnline();
      const { error } = await supabase.rpc('extend_loan_due_date' as any, {
        p_loan_id: loanId,
        p_additional_days: additionalDays,
      });

      if (error) throw error;
    },
    onSuccess: (_, { loanId }) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      posthog.capture('loan_extended', { loan_id: loanId });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
